import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {IonAlert, IonContent, IonLoading, IonRow} from '@ionic/react';
import {Order, Report, ReportRow, ReportRowComponent} from '../reportPage.component';
import {StyledFilesContainer} from "../components/file/file.style";
import CloseImage from "../../../assets/images/e-remove.svg";
import {StyledIonModal, StyledModalContent} from "../../../components/modal/modal.style";
import {StyledContent} from "./commentsModal.style";
import {getNumberOfComponent, orderDefects} from "../../../services/report.service";
import {StyledCircleButton} from "../../../components/button/button.style";
import AttachmentImage from "../../../assets/images/attachement.svg";
import CommentImage from "../../../assets/images/comment.svg";
import {RemoveRowComponentState} from "../components/report/reportRowComponentForm.component";
import ReportRowComponentEditModal from "./reportRowComponentEditModal.component";
import {Profile} from "../../../models/profile";
import {ro} from "date-fns/locale";
import {StyledComponentsContainer} from "../components/report/reportRowForm.style";
import {getNokTotal, getRowComponentsSummary, getSingleComponentStatus} from "../../../services/componentStatus";
import {StyledSingleComponentsList} from "../components/report/singleComponents.style";
import {DefectRedImage} from "../../../utils/styles/icons";

type ModalProps = {
    isOpen: boolean;
    orderId?: string,
    reportId?: string,
    profile?: Profile,
    specificationId?: string,
    row: ReportRow;
    report: Report;
    order?: Order;
    reportPreview: boolean;
    onClose: (row: ReportRow) => void;
    onRemove: (component: ReportRowComponent, key: number) => void;
    onChange: (component: ReportRowComponent, key: number) => void;
};

const ReportRowComponentsModal: React.FC<ModalProps> = (props: ModalProps) => {

    const {t} = useTranslation();
    const [showModal, updateShowModal] = useState<boolean>(props.isOpen);
    const [row, setRow] = useState<ReportRow>(props.row);
    const [selectedComponent, setSelectedComponent] = useState<ReportRowComponent|undefined>();
    const [removeRowComponentState, updateRemoveRowComponentState] = useState({ showAlert: false, component: undefined } as RemoveRowComponentState);

    const handleClose = () => {
        props.onClose(props.row);
    };

    const editComponent = (component: ReportRowComponent) => {
        setSelectedComponent(component);
    }

    useEffect(() => {
        handleOpen();
    }, [props.isOpen]);

    const handleOpen = () => {
        updateShowModal(props.isOpen);
    };

    return (
        <>
            <StyledIonModal
                isOpen={showModal}
                onDidDismiss={() => {handleClose()}}
                initialBreakpoint={0.99}
                breakpoints={[0, 0.99]}
            >
                <StyledModalContent className="modal-wrap">
                    <img src={CloseImage} className="close" onClick={() => handleClose() } />
                    <StyledContent className="content full-height">
                        <StyledComponentsContainer>
                            <div className="label">{t('reportPage.components.list.title')}{props.row.components ? ` (${props.order?.enable_single_components ? props.row.components.length : getRowComponentsSummary(props.row.components, props.order?.sub_type).total})` : ''}</div>

                            <StyledSingleComponentsList>
                                {props.row.components && props.row.components.map((component, key) => {
                                    const isSingle = !!props.order?.enable_single_components;
                                    const status = getSingleComponentStatus(component.defectGroups);
                                    const counts = getRowComponentsSummary([component], props.order?.sub_type);
                                    const componentDefects = component.defectGroups
                                        .reduce((all, group) => all.concat(group.defects || []), [] as typeof component.defectGroups[0]['defects'])
                                        .filter((defect) => defect.name || defect.other);
                                    return <div className="component-card" key={'component_card_' + key}>
                                        <div className="card-header">
                                            <StyledCircleButton disabled={true} className="white-border small">{getNumberOfComponent(props.row, component, key)}</StyledCircleButton>
                                            <span className="number">{component.componentNumberText}{!isSingle && component.quantity ? ` (${component.quantity})` : ''}</span>
                                            <div className="indicator-icons">
                                                {component.comment && <StyledCircleButton className="white" disabled={true}><img src={CommentImage} /></StyledCircleButton>}
                                                {component.files.length > 0 && <StyledCircleButton className="white" disabled={true}><img src={AttachmentImage} /></StyledCircleButton>}
                                            </div>
                                            {isSingle &&
                                                <span className={'status-badge ' + status}>{t('reportPage.components.list.' + status)}</span>
                                            }
                                            {!isSingle && counts.notReworked > 0 &&
                                                <span className="status-badge nok">{t('reportPage.components.list.nok')} ({counts.notReworked})</span>
                                            }
                                            {!isSingle && counts.reworked > 0 &&
                                                <span className="status-badge reworked">{t('reportPage.components.list.reworked')} ({counts.reworked})</span>
                                            }
                                            {!isSingle && counts.notReworked === 0 && counts.reworked === 0 &&
                                                <span className="status-badge ok">{t('reportPage.components.list.ok')}</span>
                                            }
                                        </div>
                                        {isSingle && componentDefects.map((defect, defectKey) =>
                                            <div className="defect-line" key={'defect_' + key + '_' + defectKey}>
                                                <img src={DefectRedImage} alt=""/>{defect.name || defect.other}
                                            </div>
                                        )}
                                        {!isSingle && component.defectGroups.map((defectGroup, groupKey) => {
                                            const names = (defectGroup.defects || [])
                                                .map((defect) => defect.name || defect.other)
                                                .filter((name) => name)
                                                .join(' + ');
                                            if (!names) return null;
                                            return <div className="defect-line" key={'defect_group_' + key + '_' + groupKey}>
                                                <img src={DefectRedImage} alt=""/>{names} ({getNokTotal(defectGroup, props.order?.sub_type)})
                                            </div>;
                                        })}
                                        {!props.reportPreview &&
                                            <div className="card-actions">
                                                <button type="button" className="edit" onClick={() => editComponent(component)}>{t('reportPage.components.list.edit')}</button>
                                                {props.row.components && props.row.components.length > 1 &&
                                                    <button type="button" className="delete" onClick={() => updateRemoveRowComponentState({showAlert: true, component: component})}>{t('reportPage.components.list.delete')}</button>
                                                }
                                            </div>
                                        }
                                    </div>;
                                })}
                            </StyledSingleComponentsList>

                        </StyledComponentsContainer>
                    </StyledContent>
                </StyledModalContent>

                <IonAlert
                    isOpen={removeRowComponentState.showAlert}
                    onDidDismiss={() => updateRemoveRowComponentState({showAlert: false, component: undefined})}
                    header={t('reportPage.components.list.remove')}
                    buttons={[
                        {
                            text: t('common.alertCancel'),
                            role: 'cancel',
                            cssClass: 'secondary',
                            handler: () => {
                                updateRemoveRowComponentState({showAlert: false, component: undefined})
                            },
                        },
                        {
                            text: t('common.alertConfirm'),
                            handler: async () => {
                                if (removeRowComponentState.component !== undefined) {
                                    let index = props.row.components?.findIndex(c => c.key === removeRowComponentState.component?.key) ?? -1;
                                    if (index !== -1) {
                                        props.onRemove(removeRowComponentState.component, index);
                                    }

                                }
                            }
                        }
                    ]}
                />
            </StyledIonModal>

            {selectedComponent && <ReportRowComponentEditModal
                isOpen={true}
                reportPreview={props.reportPreview}
                orderId={props.orderId}
                specificationId={props.specificationId}
                reportId={props.reportId}
                profile={props.profile}
                row={props.row}
                component={selectedComponent}
                report={props.report}
                order={props.order}
                onClose={() => {
                    setSelectedComponent(undefined);
                }}
                onSubmit={(component) => {
                    let index = props.row.components?.findIndex(c => c.key === selectedComponent?.key) ?? -1;
                    if (index !== -1) {
                        props.onChange(component, index)
                        setSelectedComponent(undefined)
                    }
                }}
            />}
        </>
    );
};

export default ReportRowComponentsModal;